import { supabase } from './supabaseClient';

export const STORAGE_BUCKET = 'marketing-assets';
export const CHANNELS = ['Instagram', 'TikTok', 'WhatsApp', 'Website', 'Other'];
export const STATUSES = ['In Review', 'Needs Rework', 'Approved'];
export const MAX_PINNED = 3;

// Custom drag MIME type used when dragging an asset card from the library
// into the planner grid. A bespoke type (rather than 'text/plain') lets the
// grid distinguish an incoming asset from one of its own tiles being
// reordered, and stops unrelated dragged text from being treated as a drop.
export const ASSET_DRAG_TYPE = 'application/x-skinfairy-asset';

export function latestVersion(asset) {
  const versions = asset.marketing_asset_versions || [];
  return versions[versions.length - 1];
}

export function publicImageUrl(path) {
  return supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path).data.publicUrl;
}

async function uploadImage(assetId, file) {
  const ext = file.name.split('.').pop();
  const path = `${assetId}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from(STORAGE_BUCKET).upload(path, file);
  if (error) throw error;
  return path;
}

export async function listAssets() {
  const { data, error } = await supabase
    .from('marketing_assets')
    .select('*, marketing_asset_versions(*)')
    .order('created_at', { ascending: false })
    .order('version_number', { foreignTable: 'marketing_asset_versions' });
  if (error) throw error;
  return data;
}

export async function addAsset({ channel, caption, created_by }, file) {
  const { data: asset, error: assetError } = await supabase
    .from('marketing_assets')
    .insert({ channel, created_by })
    .select()
    .single();
  if (assetError) throw assetError;

  const imagePath = await uploadImage(asset.id, file);

  const { error: versionError } = await supabase.from('marketing_asset_versions').insert({
    asset_id: asset.id,
    version_number: 1,
    image_path: imagePath,
    caption,
    uploaded_by: created_by,
  });
  if (versionError) throw versionError;

  return asset;
}

export async function addVersion(assetId, nextVersionNumber, { caption, uploadedBy }, file) {
  const imagePath = await uploadImage(assetId, file);

  const { error: versionError } = await supabase.from('marketing_asset_versions').insert({
    asset_id: assetId,
    version_number: nextVersionNumber,
    image_path: imagePath,
    caption,
    uploaded_by: uploadedBy,
  });
  if (versionError) throw versionError;

  const { error: statusError } = await supabase
    .from('marketing_assets')
    .update({ status: 'In Review' })
    .eq('id', assetId);
  if (statusError) throw statusError;
}

export async function setAssetStatus(id, status) {
  const { error } = await supabase.from('marketing_assets').update({ status }).eq('id', id);
  if (error) throw error;
}

export async function setPinned(id, pinned) {
  const { error } = await supabase
    .from('marketing_assets')
    .update({ pinned_at: pinned ? new Date().toISOString() : null })
    .eq('id', id);
  if (error) throw error;
}

// The queue of posts you plan to publish, in the order they'd land on the
// real grid. Returns a plain ordered list — no fixed length, no null padding
// — because the planner grid now stacks this queue on top of the live
// Instagram feed rather than filling a standalone 9-slot mock.
//
// Two pools, deliberately:
//   - APPROVED Instagram assets queue up automatically (approved = ready to
//     go out), ordered pinned-first then newest;
//   - anything MANUALLY PLACED (dragged in, so it has a feed_position) counts
//     whatever its status, because the point of planning is to see a draft in
//     position *before* approving it. A draft never appears on its own.
// A manual position always wins over the automatic order.
export function buildPlannedQueue(assets) {
  const instagram = assets.filter((a) => a.channel === 'Instagram');

  const approved = instagram.filter((a) => a.status === 'Approved');
  const pinned = approved
    .filter((a) => a.pinned_at)
    .sort((a, b) => new Date(b.pinned_at) - new Date(a.pinned_at));
  const rest = approved
    .filter((a) => !a.pinned_at)
    .sort((a, b) => new Date(latestVersion(b)?.uploaded_at) - new Date(latestVersion(a)?.uploaded_at));

  const placed = instagram
    .filter((a) => a.feed_position != null)
    .sort((a, b) => a.feed_position - b.feed_position);
  const placedIds = new Set(placed.map((a) => a.id));

  const automatic = [...pinned, ...rest].filter((a) => !placedIds.has(a.id));

  // Placed items sit at their saved index; the automatic queue fills the gaps
  // around them, so dragging one post doesn't reshuffle everything else.
  const queue = [];
  const remaining = [...automatic];
  const maxIndex = placed.length ? Math.max(...placed.map((a) => a.feed_position)) : -1;
  const length = Math.max(maxIndex + 1, placed.length + automatic.length);

  for (let i = 0; i < length; i++) {
    const atIndex = placed.find((a) => a.feed_position === i);
    if (atIndex) queue.push(atIndex);
    else if (remaining.length) queue.push(remaining.shift());
  }
  return [...queue, ...remaining];
}

// Persists the planned order: orderedIds[i] is the asset now at position i.
export async function setFeedOrder(orderedIds) {
  await Promise.all(
    orderedIds.map((id, index) =>
      id ? supabase.from('marketing_assets').update({ feed_position: index }).eq('id', id) : null
    )
  );
}

export async function clearFeedOrder(assetIds) {
  await Promise.all(
    assetIds.map((id) => supabase.from('marketing_assets').update({ feed_position: null }).eq('id', id))
  );
}

export async function deleteAsset(id) {
  const { error } = await supabase.from('marketing_assets').delete().eq('id', id);
  if (error) throw error;
}

// Comment counts for every asset in one query, tallied client-side. The
// alternative — a count per card — is an N+1 across the whole library just
// to render a number in a card footer.
export async function listCommentCounts() {
  const { data, error } = await supabase.from('marketing_comments').select('asset_id');
  if (error) throw error;
  return data.reduce((acc, row) => {
    acc[row.asset_id] = (acc[row.asset_id] || 0) + 1;
    return acc;
  }, {});
}

export async function listComments(assetId) {
  const { data, error } = await supabase
    .from('marketing_comments')
    .select('*, author:profiles(id, display_name, email)')
    .eq('asset_id', assetId)
    .order('created_at');
  if (error) throw error;
  return data;
}

export async function addComment(assetId, authorId, body) {
  const { data, error } = await supabase
    .from('marketing_comments')
    .insert({ asset_id: assetId, author_id: authorId, body })
    .select('*, author:profiles(id, display_name, email)')
    .single();
  if (error) throw error;
  return data;
}
